import { useApplicationFormContext } from './ApplicationForm'
import { FileData } from '@applyboard/school-applications-types-lib'
import { GenericError, additionalDocumentTypeOptions, hasApplicationFee } from '../../../utils'
import { JsonApiError } from '../../../utils/errors'
import { ApplicationFormCard } from './ApplicationFormCard'
import { useUpdateApplication } from '../../../hooks'
import { DocumentTags } from '../../../utils/enums'
import { getFilesOfType } from './utils'
import { FormTab } from './FormTab'
import { ApplicationFormsProps } from './ApplicationForms'
import { TimelineObject, useGetTimelineItems } from '../../../hooks/useGetTimelineItems'
import styled from '@emotion/styled'

export function ApplicationFormWrapper(props: Readonly<ApplicationFormsProps>) {
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })
  const timelineItems = useGetTimelineItems(props.application)
  const lastTimeline = timelineItems.at(-1)
  const totalCards = timelineItems.length
  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()
  const cardInfo = getCardInformation(props.selectedTab, timelineItems)

  const onError = (err: GenericError) => {
    if (err instanceof JsonApiError && err.code === 'applicationNotDraft')
      return props.onError?.(new Error(''))
    if (err instanceof Error) {
      props.onError?.(err)
    }
  }

  return (
    <Wrapper>
      <ApplicationFormCard
        cardNumber={cardInfo?.cardNumber ?? 0}
        totalCards={totalCards}
        icon={cardInfo?.icon}
        title={cardInfo?.title ?? ''}
        isLoading={isUpdatingApplication}
        disabled={props.disabled}
        formId={cardInfo?.formId ?? ''}
        focusRef={props.focusRef}
        submitButtonLabel={
          cardInfo?.cardNumber === lastTimeline?.cardNumber
            ? hasApplicationFee(props.application)
              ? 'Pay & Submit'
              : 'Save & Submit'
            : undefined
        }
        onPreviousTabButton={props.onPreviousTabButton}
        onSubmitApplication={
          cardInfo?.cardNumber === lastTimeline?.cardNumber
            ? () => {
                if (props.application) {
                  props.onSuccess({ data: props.application })
                }
                updateApplication(
                  {
                    files: pendingFileUploadState,
                  },
                  {
                    onSuccess: response => {
                      resetFiles(
                        getFilesOfType(
                          Object.keys(additionalDocumentTypeOptions) as DocumentTags[],
                          response.data?.attributes?.files as FileData,
                        ),
                      )
                      if (props.submitApplication) props.submitApplication() // typescript is confused...
                    },
                    onError,
                  },
                )
              }
            : undefined
        }
      >
        <FormTab
          application={props.application}
          disabled={props.disabled}
          onError={onError}
          onSuccess={props.onSuccess}
          selectedTab={props.selectedTab}
          updateApplication={updateApplication}
          submitApplication={props.submitApplication}
          formId={cardInfo?.formId ?? ''}
        />
      </ApplicationFormCard>
    </Wrapper>
  )
}

/*
In relation with currentStep, selectedStep can go out of bound, assume we're on the last step
 */
function getCardInformation(selectedTab: number, timelineItems: TimelineObject[]) {
  return timelineItems.find(item => item.key === selectedTab)
}

const Wrapper = styled.div({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  maxWidth: '100%',
})
